import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, NavLink, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import Library from './pages/Library';
import Albums from './pages/Albums';
import AlbumDetail from './pages/AlbumDetail';
import Artists from './pages/Artists';
import Playlists from './pages/Playlists';
import PlaylistDetail from './pages/PlaylistDetail';
import BestOf from './pages/BestOf';
import SmartPlaylistBuilder from './pages/SmartPlaylistBuilder';
import NewImports from './pages/NewImports';
import Importer from './pages/Importer';
import Player from './components/Player';
import ProtectedRoute from './components/ProtectedRoute';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ArtFetcher from './pages/ArtFetcher';
import useAuthStore from './store/authStore';
import { logout, logPageView } from './api';
import './App.css';
import InviteManager from './pages/InviteManager';
import Analytics from './pages/Analytics';
import RemoteSync from './pages/RemoteSync';

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30000, refetchOnWindowFocus: false } }
});

function Sidebar() {
  const { user, clearAuth } = useAuthStore();
  console.log('Sidebar user:', user);
  const navigate = useNavigate();
  const linkClass = ({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`;

  const handleLogout = async () => {
    try { await logout(); } catch {}
    clearAuth();
    navigate('/login');
  };

  return (
    <nav className="sidebar">
      <div className="sidebar-logo">🎵 bbmusicmix</div>
      <div className="sidebar-section">
        <div className="sidebar-label">Library</div>
        <NavLink to="/" end className={linkClass}>Songs</NavLink>
        <NavLink to="/albums" className={linkClass}>Albums & Artists</NavLink>
      </div>
      <div className="sidebar-section">
        <div className="sidebar-label">Playlists</div>
        <NavLink to="/playlists" end className={linkClass}>All Playlists</NavLink>
        <NavLink to="/bestof" className={linkClass}>⭐ Best Of</NavLink>
        <NavLink to="/new-imports" className={linkClass}>🆕 New Music</NavLink>
        <NavLink to="/playlists/new/smart" className={linkClass}>+ New Smart Playlist</NavLink>
      </div>
      {user?.is_staff === true && (
        <div className="sidebar-section">
          <div className="sidebar-label">Tools</div>
          <NavLink to="/import" className={linkClass}>Import from iTunes</NavLink>
          <NavLink to="/art" className={linkClass}>🖼 Album Art</NavLink>
          <NavLink to="/invites" className={linkClass}>👥 Invite Manager</NavLink>
          <NavLink to="/analytics" className={linkClass}>📊 Analytics</NavLink>
          <NavLink to="/sync" className={linkClass}>🔄 Remote Sync</NavLink>
        </div>
      )}
      {user && (
        <div style={{ marginTop: 'auto', padding: '16px 12px', borderTop: '1px solid var(--border)' }}>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
            Signed in as <strong style={{ color: 'var(--text)' }}>{user.username}</strong>
          </div>
          <button
            onClick={handleLogout}
            style={{
              background: 'none', border: '1px solid var(--border)', borderRadius: 6,
              color: 'var(--text-muted)', cursor: 'pointer', fontSize: 12,
              padding: '5px 12px', width: '100%',
            }}
          >
            Sign Out
          </button>
        </div>
      )}
    </nav>
  );
}

function BottomNav() {
  const user = useAuthStore(state => state.user);
  const isAdmin = Boolean(user?.is_staff);
  const linkClass = ({ isActive }) => `bottom-nav-link ${isActive ? 'active' : ''}`;

  return (
    <nav className="bottom-nav">
      <NavLink to="/songs" className={linkClass}>
        <span className="bottom-nav-icon">♫</span>
        Songs
      </NavLink>
      <NavLink to="/albums" className={linkClass}>
        <span className="bottom-nav-icon">💿</span>
        Albums
      </NavLink>
      <NavLink to="/playlists" className={linkClass}>
        <span className="bottom-nav-icon">≡</span>
        Playlists
      </NavLink>
      <NavLink to="/bestof" className={linkClass}>
        <span className="bottom-nav-icon">⭐</span>
        Best Of
      </NavLink>
      {isAdmin && (
        <NavLink to="/invites" className={linkClass}>
          <span className="bottom-nav-icon">👥</span>
          Invites
        </NavLink>
      )}
      {isAdmin && (
        <NavLink to="/analytics" className={linkClass}>
          <span className="bottom-nav-icon">📊</span>
          Analytics
        </NavLink>
      )}
    </nav>
  );
}

function AdminRoute({ children }) {
  const { user } = useAuthStore();
  if (!user?.is_staff) return <Navigate to="/" replace />;
  return children;
}

function PageTracker() {
  const location = useLocation();
  const { user } = useAuthStore();
  useEffect(() => {
    if (user) logPageView(location.pathname).catch(() => {});
  }, [location.pathname, user]);
  return null;
}

function AppContent() {
  const { loadUser } = useAuthStore();

  useEffect(() => { loadUser(); }, []);

  return (
    <>
    <PageTracker />
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password/:uid/:token" element={<ResetPasswordPage />} />
      <Route path="/*" element={
        <ProtectedRoute>
          <div className="app-layout">
            <Sidebar />
            <main className="main-content">
              <Routes>
                <Route path="/" element={
                  window.matchMedia('(max-width: 768px)').matches
                    ? <Navigate to="/bestof" replace />
                    : <Library />
                } />
                <Route path="/songs" element={<Library />} />
                <Route path="/albums" element={<Albums />} />
                <Route path="/albums/:id" element={<AlbumDetail />} />
                <Route path="/artists" element={<Artists />} />
                <Route path="/playlists" element={<Playlists />} />
                <Route path="/bestof" element={<BestOf />} />
                <Route path="/new-imports" element={<NewImports />} />
                <Route path="/playlists/new/smart" element={<SmartPlaylistBuilder />} />
                <Route path="/playlists/:id" element={<PlaylistDetail />} />
                <Route path="/playlists/:id/edit" element={<SmartPlaylistBuilder />} />
                <Route path="/import" element={<AdminRoute><Importer /></AdminRoute>} />
                <Route path="/art" element={<AdminRoute><ArtFetcher /></AdminRoute>} />
                <Route path="/invites" element={<AdminRoute><InviteManager /></AdminRoute>} />
                <Route path="/analytics" element={<AdminRoute><Analytics /></AdminRoute>} />
                <Route path="/sync" element={<AdminRoute><RemoteSync /></AdminRoute>} />
              </Routes>
            </main>
            <Player />
            <BottomNav />
          </div>
        </ProtectedRoute>
      } />
    </Routes>
    </>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </QueryClientProvider>
  );
}